/**
 * «Бүх гутал»-ын шүүлтүүр — хайлт, төлөв, нэгж, нийлүүлэгч, гарал, үнэ.
 *
 * Төлөв ба нэгж нь ТООТОЙ товч (өмнө нь select байв): нэг дарж солино,
 * дарахаасаа өмнө хэд гарахыг харна. Тоо нь `GET /items/facets`-ээс —
 * тухайн товчны өөрийн шүүлтүүрийг хасч бодсон тул 38 размер сонгосон
 * үед «Салбар 1 · 20» гэдэг нь «38-аас Салбар 1-д 20» гэсэн үг.
 */

import { formatQty } from '@boots/domain';
import type { ItemFacetsOutput } from '@boots/schema';
import { useState, type ReactNode } from 'react';
import type { SupplierRow } from '../supplier-form';
import { Chip, Tabs } from '../ui';
import {
  CLEAR_FILTERS, activeFilterCount, type ItemsFilter, type SetItemsFilter, type StatusScope,
} from '../../lib/use-items-filter';
import { STATUS_LABEL, type ItemStatus, type Location, type Store } from '../../lib/types';

export function ItemFilterBar({
  filter, set, facets, stores, locations, suppliers, onSearch, actions,
}: {
  filter: ItemsFilter;
  set: SetItemsFilter;
  /** Ачаалж дуустал `undefined` — тэр үед товчнууд тоогүй харагдана */
  facets: ItemFacetsOutput | undefined;
  stores: Store[];
  locations: Location[];
  suppliers: SupplierRow[];
  onSearch: (text: string) => void;
  actions?: ReactNode;
}) {
  const active = activeFilterCount(filter);
  const locCount = new Map(facets?.locations.map((l) => [l.locationId, l.count]));
  const supCount = new Map(facets?.suppliers.map((s) => [s.supplierId, s.count]));
  const withCount = (label: string, n: number | undefined) =>
    facets ? `${label} · ${formatQty(n ?? 0)}` : label;

  return (
    <section className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-4 py-3 sm:px-card">
      <div className="flex flex-wrap items-center gap-2">
        {/* Баркод уншуулах = гар шивэлт + Enter — тусдаа горим хэрэггүй */}
        <SearchBox key={filter.q} value={filter.q} onSubmit={onSearch} />
        <Tabs
          scroll
          tabs={statusTabs(filter.status, facets?.statuses)}
          value={filter.status}
          onChange={(v) => set({ status: v || null })}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Tabs
          scroll
          tabs={storeTabs(stores, facets?.stores)}
          value={filter.storeId}
          onChange={(v) => set({ storeId: v || null, locationId: null })}
        />
        {/* Нэгж сонгосон бол зөвхөн ТҮҮНИЙ байршил — 20 мөрөөс хайхгүй */}
        {filter.storeId ? (
          <FilterSelect
            placeholder="Бүх байршил"
            value={filter.locationId}
            onChange={(v) => set({ locationId: v })}
            options={locations
              .filter((l) => l.storeId === filter.storeId)
              .map((l) => ({ value: l.id, label: withCount(l.name, locCount.get(l.id)) }))}
          />
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          placeholder="Бүх нийлүүлэгч"
          value={filter.supplierId}
          onChange={(v) => set({ supplierId: v })}
          options={suppliers.map((s) => ({ value: s.id, label: withCount(s.name, supCount.get(s.id)) }))}
        />

        {/* Аялал ба «Эхний үлдэгдэл» — хоёр тусдаа бүртгэл */}
        <FilterSelect
          placeholder="Аялал + эхний үлдэгдэл"
          value={filter.origin}
          onChange={(v) => set({ origin: v })}
          options={[
            { value: 'trip', label: withCount('Аяллаар ирсэн', facets?.origins.trip) },
            { value: 'opening', label: withCount('Эхний үлдэгдэл', facets?.origins.opening) },
          ]}
        />

        <PriceRange
          key={`${filter.priceMin}|${filter.priceMax}`}
          min={filter.priceMin}
          max={filter.priceMax}
          onApply={(min, max) => set({ priceMin: min, priceMax: max })}
        />

        {/* Агуулахын ажлын дараалал — байгаа үед л гарна */}
        <WorkChip label="Үнэгүй" on={filter.unpriced} count={facets?.unpriced}
          onToggle={() => set({ unpriced: filter.unpriced ? null : 'true' })} />
        <WorkChip label="Шошгогүй" on={filter.unlabeled} count={facets?.unlabeled}
          onToggle={() => set({ unlabeled: filter.unlabeled ? null : 'true' })} />

        {active > 0 ? <Chip onClick={() => set(CLEAR_FILTERS)}>Цэвэрлэх ({formatQty(active)})</Chip> : null}

        {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
      </div>
    </section>
  );
}

/**
 * «Нөөцөд» + нөөцөөс ГАДУУРХ төлөв бүр (байгаа бол) + «Бүгд».
 *
 * Аль төлөв «Нөөцөд»-д орохыг сервер хэлнэ (`inStock`) — жагсаалтыг
 * энд хуулахгүй. Агуулахын хуудаснаас `?status=AVAILABLE` гэж ирвэл
 * тэр төлөв өөрийн товчтой гарна.
 */
function statusTabs(value: StatusScope, rows: ItemFacetsOutput['statuses'] | undefined) {
  const byStatus = new Map(rows?.map((r) => [r.status, r]));
  const sum = (pick: (r: ItemFacetsOutput['statuses'][number]) => boolean) =>
    rows ? rows.filter(pick).reduce((n, r) => n + r.count, 0) : undefined;
  const own = (Object.keys(STATUS_LABEL) as ItemStatus[]).filter((s) => {
    const r = byStatus.get(s);
    return s === value || (r !== undefined && !r.inStock && r.count > 0);
  });

  return [
    { key: '' as StatusScope, label: 'Нөөцөд', count: sum((r) => r.inStock) },
    ...own.map((s) => ({ key: s as StatusScope, label: STATUS_LABEL[s], count: rows ? (byStatus.get(s)?.count ?? 0) : undefined })),
    { key: 'all' as StatusScope, label: 'Бүгд', count: sum(() => true) },
  ];
}

function storeTabs(stores: Store[], rows: ItemFacetsOutput['stores'] | undefined) {
  const byStore = new Map(rows?.map((r) => [r.storeId, r.count]));
  return [
    { key: '', label: 'Бүх нэгж', count: rows?.reduce((n, r) => n + r.count, 0) },
    ...stores.map((s) => ({
      key: s.id,
      // Агуулахын нэр «агуулах» гэж заагаагүй бол нэмнэ — салбартай андуурахгүй
      label: s.type === 'WAREHOUSE' && !/агуулах/i.test(s.name) ? `${s.name} · агуулах` : s.name,
      count: rows ? (byStore.get(s.id) ?? 0) : undefined,
    })),
  ];
}

/** Шошгогүй, үнэгүй — 0 бол товч харагдахгүй (идэвхтэй үед л үлдэнэ). */
function WorkChip({ label, on, count, onToggle }: {
  label: string;
  on: boolean;
  count: number | undefined;
  onToggle: () => void;
}) {
  if (!on && !count) return null;
  return (
    <Chip active={on} onClick={onToggle}>
      {label}
      {count === undefined ? null : <span className="num ml-1.5 font-medium">{formatQty(count)}</span>}
    </Chip>
  );
}

/**
 * Үнийн муж, ₮. Enter эсвэл талбараас гарахад үйлчилнэ — үсэг бүрд
 * хүсэлт явуулахгүй. Доод нь дээдээсээ их бол сольж тавина.
 */
function PriceRange({ min, max, onApply }: {
  min: string;
  max: string;
  onApply: (min: string | null, max: string | null) => void;
}) {
  const [lo, setLo] = useState(min);
  const [hi, setHi] = useState(max);

  const apply = () => {
    if (lo === min && hi === max) return;
    const swap = lo && hi && Number(lo) > Number(hi);
    onApply((swap ? hi : lo) || null, (swap ? lo : hi) || null);
  };

  const field = (value: string, onChange: (v: string) => void, label: string) => (
    <input
      value={value ? formatQty(value) : ''}
      // Зөвхөн цифр; таслалыг дэлгэцэнд л нэмнэ. 999 сая хүртэл.
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 9))}
      onBlur={apply}
      onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
      inputMode="numeric"
      aria-label={`Үнэ — ${label}`}
      placeholder={label}
      className={`num min-h-11 w-26 rounded-full border px-3.5 py-1 text-right text-base
        placeholder:text-ink-3 focus:border-accent focus:outline-none sm:min-h-0 sm:text-xs ${
        value ? 'border-accent-700 bg-accent-100 text-accent-700' : 'border-hair bg-panel text-ink'
      }`}
    />
  );

  return (
    <div className="flex items-center gap-1.5 text-xs text-ink-2">
      <span>Үнэ</span>
      {field(lo, setLo, 'доод')}
      <span aria-hidden>–</span>
      {field(hi, setHi, 'дээд')}
      <span aria-hidden>₮</span>
    </div>
  );
}

/**
 * Шүүлтүүрийн мөрөнд багтах нарийн `select`. Хоосон утга нь «Бүх …»
 * гэж өөрөө нэрлэгдэнэ; сонгогдсон бол accent өнгөөр ялгарна.
 */
function FilterSelect({ placeholder, value, onChange, options }: {
  placeholder: string;
  value: string;
  onChange: (v: string | null) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value || null)}
      className={`min-h-11 max-w-55 min-w-29.5 rounded-full border px-3.5 py-1
        text-base focus:border-accent focus:outline-none sm:min-h-0 sm:text-xs ${
        value ? 'border-accent-700 bg-accent-100 text-accent-700' : 'border-hair bg-panel text-ink-2'
      }`}
      aria-label={placeholder}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/**
 * Скан ба кодоор хайх — тусдаа «Скан» хуудсыг ОРЛОНО.
 *
 * Баркод уншигч нь гар шиг бичээд Enter дардаг: талбар `autoFocus` тул
 * хуудас нээгдмэгц уншуулж болно. Юу хийхийг дуудагч шийднэ
 * (`ItemsPage.submitSearch`): бүтэн серийн дугаар → тэр гутал, «38» →
 * размер, бусад → хайлт.
 *
 * Талбар нь URL-ийн `q`-тэй `key`-ээр сэргэнэ (header-ийн хайлтаас
 * ирсэн утга ч харагдана).
 */
function SearchBox({ value, onSubmit }: { value: string; onSubmit: (v: string) => void }) {
  const [text, setText] = useState(value);

  return (
    <form
      className="flex w-full items-center gap-1.5 sm:w-auto"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(text.trim());
      }}
    >
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        type="search"
        autoFocus
        aria-label="Сканнердах, серийн дугаар эсвэл размераар хайх"
        placeholder="Скан, код эсвэл размер (38)…"
        className="min-h-11 min-w-0 flex-1 rounded-full border border-hair bg-field
          px-4 py-1 text-base text-ink placeholder:text-ink-3 focus:border-accent
          focus:bg-panel focus:outline-none sm:min-h-0 sm:w-64 sm:flex-none sm:text-xs"
      />
      {value ? (
        <Chip onClick={() => { setText(''); onSubmit(''); }}>Цэвэрлэх</Chip>
      ) : null}
    </form>
  );
}
